import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";

import requestCallbackImage from "../assets/requestCallBack.jpg";

const CRM_API_URL =
   "https://api.practicalinfosec.com/api";

const SUPPORT_WHATSAPP_NUMBER = "919080432374";
const SUPPORT_MESSAGE = "Hi, I am Looking for Cyber Security Training";

const countryCodes = [
  { name: "India", code: "+91" },
  { name: "United States", code: "+1" },
  { name: "United Kingdom", code: "+44" },
  { name: "United Arab Emirates", code: "+971" },
  { name: "Singapore", code: "+65" },
  { name: "Australia", code: "+61" },
  { name: "Canada", code: "+1" },
  { name: "Germany", code: "+49" },
  { name: "France", code: "+33" },
  { name: "Netherlands", code: "+31" },
  { name: "Saudi Arabia", code: "+966" },
  { name: "Qatar", code: "+974" },
  { name: "Kuwait", code: "+965" },
  { name: "Oman", code: "+968" },
  { name: "Malaysia", code: "+60" },
  { name: "Sri Lanka", code: "+94" },
  { name: "Nepal", code: "+977" },
  { name: "Bangladesh", code: "+880" },
];

const contactCards = [
  {
    icon: "fa-solid fa-phone-volume",
    title: "Call Us",
    value: "+91-9080432374",
    description: "Speak with our course counselling team.",
    href: "tel:+91-9080432374",
    action: "Call Now",
  },
  {
    icon: "fa-solid fa-envelope-open-text",
    title: "Email Support",
    value: "support@practicalinfosec.com",
    description: "Send your course, batch, or support queries.",
    href: "mailto:support@practicalinfosec.com",
    action: "Send Email",
  },
  {
    icon: "fa-brands fa-whatsapp",
    title: "WhatsApp",
    value: "+91-9080432374",
    description: "Get quick guidance on the right cybersecurity course.",
    href: "https://wa.me/919080432374",
    action: "Chat Now",
  },
];

const offices = [
  {
    label: "REGIONAL OFFICE",
    title: "Chennai Office",
    address: [
      "Plot No.70, Journalist Colony, 30 Feet Road,",
      "Thiruvanmiyur, Chennai,",
      "Tamil Nadu - 600014",
    ],
    icon: "fa-solid fa-building",
    theme: "from-blue-700 to-cyan-500",
  },
  {
    label: "REGISTERED OFFICE",
    title: "Guntur Office",
    address: ["8-31/3; Kaza, Mangalagiri, Guntur,", "Andhra Pradesh - 522503"],
    icon: "fa-solid fa-location-dot",
    theme: "from-purple-700 to-blue-700",
  },
];

const supportItems = [
  "Course counselling",
  "Batch details",
  "Fee and installment guidance",
  "Corporate training enquiries",
  "Student support",
  "Career roadmap guidance",
];

function validateEmailValue(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function validatePhoneValue(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 8;
}

function ContactUs() {
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Contact Us | Practical Infosec";

    function handleScroll() {
      setShowBackToTop(window.scrollY > 300);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function updateFormField(field, value) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  }

  function validateForm() {
    const nextErrors = {};

    if (formData.name.trim().length < 3) {
      nextErrors.name = "Please enter your full name.";
    }

    if (!validateEmailValue(formData.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!validatePhoneValue(formData.phone)) {
      nextErrors.phone = "Please enter a valid phone number.";
    }

    if (formData.message.trim().length < 5) {
      nextErrors.message = "Please enter your message.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  async function handleContactSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Check the form",
        text: "Please fill all required fields correctly.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const payload = {
      FullName: formData.name.trim(),
      Email: formData.email.trim(),
      PhoneNumber: `${formData.countryCode} ${formData.phone.trim()}`,
      LeadCategory: "STUDENT",
      LeadSource: "ORGANIC",
      EnrollmentType: "CONTACT US",
      CollegeName: "Contact Us Page",
      PassedOutYear: "2026",
      AvailabilityBatch: "NA",
      BatchDay: "NA",
      CallBack: "YES",
      Message: formData.message.trim(),
    };

    Swal.fire({
      title: "Please Wait...",
      html: "Submitting your callback request...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await axios.post(`${CRM_API_URL}/leads/form-callback-student`, payload, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      confetti({
        particleCount: 140,
        spread: 70,
        origin: { y: 0.6 },
      });

      Swal.fire({
        icon: "success",
        title: "Request Submitted!",
        text: "Our team will contact you shortly.",
        confirmButtonColor: "#2563eb",
      });

      setFormData({
        name: "",
        email: "",
        countryCode: "+91",
        phone: "",
        message: "",
      });

      setErrors({});

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "contact_form_submit",
        lead_source: "CONTACT US",
        page: "Contact Us",
      });

      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
    } catch (error) {
      console.error("Contact form submission failed:", error);

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Unable to submit your request. Please try again.",
        confirmButtonColor: "#dc2626",
      });
    }
  }

  function openWhatsAppChat() {
    const encodedMessage = encodeURIComponent(SUPPORT_MESSAGE);
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }

  function openWhatsAppAppFallback() {
    const encodedMessage = encodeURIComponent(SUPPORT_MESSAGE);
    const deepLink = `whatsapp://send?phone=${SUPPORT_WHATSAPP_NUMBER}&text=${encodedMessage}`;
    const webLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.location.href = deepLink;

    setTimeout(() => {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }, 700);
  }

  return (
    <main className="overflow-hidden bg-white">
      {/* HERO */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-950 md:px-20 md:py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.36),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.26),transparent_36%),radial-gradient(circle_at_center,rgba(124,58,237,0.18),transparent_45%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/70 to-transparent"></div>
        </div>

        <div className="relative z-10 grid items-center gap-12 mx-auto max-w-7xl lg:grid-cols-[1fr_0.9fr]">
          <div>
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15 backdrop-blur">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Practical Infosec Support Desk
            </span>

            <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight text-white md:text-6xl">
              Speak With Our{" "}
              <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                Cybersecurity Training Team
              </span>
            </h1>

            <p className="max-w-3xl mt-6 text-lg font-semibold leading-8 text-slate-300">
              Got questions about our courses, batches, pricing, career roadmap,
              or learning platform? Share your details and our team will guide
              you personally.
            </p>

            <div className="grid grid-cols-1 gap-4 mt-10 sm:grid-cols-3">
              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">Fast</p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Callback Support
                </p>
              </div>

              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">Live</p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Course Guidance
                </p>
              </div>

              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">Career</p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Roadmap Help
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-4 mt-8 sm:flex-row">
              <a
                href="tel:+91-9080432374"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition shadow-xl px-7 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 shadow-blue-700/30 hover:scale-105"
              >
                <i className="mr-2 fa-solid fa-phone"></i>
                Call Now
              </a>

              <button
                type="button"
                onClick={openWhatsAppChat}
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                <i className="mr-2 fa-brands fa-whatsapp"></i>
                WhatsApp Us
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-r from-blue-500/40 via-cyan-400/30 to-purple-500/30 blur-2xl"></div>

            <div className="relative overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur">
              <img
                src={requestCallbackImage}
                alt="Request Callback"
                className="object-cover w-full h-[420px]"
              />

              <div className="p-6 border-t bg-slate-950/95 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Need Course Help?
                </p>

                <h2 className="mt-2 text-2xl font-black text-white">
                  Request a Callback
                </h2>

                <p className="mt-3 text-sm font-semibold leading-6 text-slate-300">
                  Our team can help you choose the right cybersecurity program
                  based on your current level and career goal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="relative px-4 py-16 overflow-hidden bg-white md:px-20">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-blue-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 rounded-full w-96 h-96 bg-cyan-200/20 blur-3xl"></div>

        <div className="relative z-10 grid grid-cols-1 gap-6 mx-auto max-w-7xl md:grid-cols-3">
          {contactCards.map((card) => (
            <a
              key={card.title}
              href={card.href}
              target={card.href.startsWith("http") ? "_blank" : undefined}
              rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
              className="p-6 transition bg-white border shadow-lg group rounded-3xl border-slate-200 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl"
            >
              <div className="flex items-center justify-center w-16 h-16 text-2xl text-white transition rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 group-hover:scale-110">
                <i className={card.icon}></i>
              </div>

              <h3 className="mt-6 text-2xl font-black text-slate-950">
                {card.title}
              </h3>

              <p className="mt-2 text-lg font-black text-blue-700">
                {card.value}
              </p>

              <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                {card.description}
              </p>

              <p className="mt-5 text-sm font-black text-slate-950">
                {card.action} →
              </p>
            </a>
          ))}
        </div>
      </section>

      {/* REQUEST CALLBACK FORM */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-50 md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              Request a Callback
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              📲 Let Our Team Contact You
            </h2>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-600">
              Want to know more about our course, schedule, pricing, or career
              path? Drop your details and we’ll get back to you as soon as
              possible.
            </p>
          </div>

          <div className="grid items-center grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1fr]">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-blue-200 via-cyan-100 to-purple-200 blur-2xl"></div>

              <div className="relative p-4 bg-white border shadow-2xl rounded-[2rem] border-slate-200">
                <img
                  src={requestCallbackImage}
                  alt="Request Callback"
                  className="object-cover w-full h-[480px] rounded-[1.5rem]"
                />

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                    <p className="text-xl font-black text-blue-700">
                      Course
                    </p>
                    <p className="mt-1 text-xs font-bold text-slate-600">
                      Junior & Senior tracks
                    </p>
                  </div>

                  <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                    <p className="text-xl font-black text-cyan-700">Batch</p>
                    <p className="mt-1 text-xs font-bold text-slate-600">
                      Timings & enrollment help
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form
              onSubmit={handleContactSubmit}
              className="relative overflow-hidden bg-white border shadow-2xl rounded-[2rem] border-slate-200"
            >
              <div className="px-6 py-6 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Contact Form
                </p>

                <h3 className="mt-2 text-3xl font-black text-white">
                  📞 Submit Request
                </h3>

                <p className="mt-2 text-sm font-semibold leading-6 text-blue-100">
                  Fill the form below. Our counselling team will contact you.
                </p>
              </div>

              <div className="p-5 space-y-5 sm:p-6">
                <div>
                  <label className="block mb-2 text-sm font-black text-slate-800">
                    Full Name
                  </label>

                  <input
                    type="text"
                    value={formData.name}
                    onChange={(event) =>
                      updateFormField("name", event.target.value)
                    }
                    placeholder="👤 Your Name"
                    className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                      errors.name
                        ? "border-red-500"
                        : formData.name.trim().length >= 3
                        ? "border-green-500"
                        : "border-slate-200 focus:border-blue-500"
                    }`}
                  />

                  {errors.name && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-black text-slate-800">
                    Email Address
                  </label>

                  <input
                    type="email"
                    value={formData.email}
                    onChange={(event) =>
                      updateFormField("email", event.target.value)
                    }
                    placeholder="📧 Email Address"
                    className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                      errors.email
                        ? "border-red-500"
                        : validateEmailValue(formData.email)
                        ? "border-green-500"
                        : "border-slate-200 focus:border-blue-500"
                    }`}
                  />

                  {errors.email && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-black text-slate-800">
                    Phone Number
                  </label>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[170px_1fr]">
                    <select
                      value={formData.countryCode}
                      onChange={(event) =>
                        updateFormField("countryCode", event.target.value)
                      }
                      className="w-full px-3 py-3 text-sm font-bold bg-white border shadow-sm outline-none rounded-xl border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    >
                      {countryCodes.map((country) => (
                        <option
                          key={`${country.name}-${country.code}`}
                          value={country.code}
                        >
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>

                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(event) =>
                        updateFormField("phone", event.target.value)
                      }
                      placeholder="📞 Contact Number"
                      className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                        errors.phone
                          ? "border-red-500"
                          : validatePhoneValue(formData.phone)
                          ? "border-green-500"
                          : "border-slate-200 focus:border-blue-500"
                      }`}
                    />
                  </div>

                  {errors.phone && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-black text-slate-800">
                    Your Message
                  </label>

                  <textarea
                    value={formData.message}
                    onChange={(event) =>
                      updateFormField("message", event.target.value)
                    }
                    placeholder="💬 Your Message"
                    maxLength={500}
                    rows={4}
                    className={`w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none resize-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
                      errors.message
                        ? "border-red-500"
                        : formData.message.trim().length >= 5
                        ? "border-green-500"
                        : "border-slate-200 focus:border-blue-500"
                    }`}
                  />

                  <div className="flex items-center justify-between gap-3 mt-1">
                    {errors.message ? (
                      <p className="text-xs font-semibold text-red-600">
                        {errors.message}
                      </p>
                    ) : (
                      <p className="text-xs font-semibold text-slate-500">
                        Tell us about your requirement.
                      </p>
                    )}

                    <p className="text-xs font-black text-slate-500">
                      {formData.message.length}/500
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 text-center border rounded-xl bg-slate-50 border-slate-200">
                    <p className="text-xs font-black text-blue-700">Course</p>
                  </div>

                  <div className="p-3 text-center border rounded-xl bg-slate-50 border-slate-200">
                    <p className="text-xs font-black text-purple-700">Fee</p>
                  </div>

                  <div className="p-3 text-center border rounded-xl bg-slate-50 border-slate-200">
                    <p className="text-xs font-black text-cyan-700">Career</p>
                  </div>
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center w-full px-6 py-4 text-base font-black text-white transition shadow-xl cursor-pointer rounded-xl bg-gradient-to-r from-blue-700 via-cyan-600 to-blue-700 shadow-blue-700/20 hover:scale-[1.02] hover:shadow-2xl"
                >
                  📞 Submit Request →
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* OFFICES */}
      <section className="relative px-4 py-20 overflow-hidden bg-white md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              Company Details
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              PRACTICAL INFOSEC PRIVATE LIMITED
            </h2>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-600">
              Reach us through our office locations or contact our support team
              directly for training, course, and partnership enquiries.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {offices.map((office) => (
              <div
                key={office.label}
                className="relative overflow-hidden transition border shadow-xl rounded-3xl border-slate-200 hover:-translate-y-2 hover:shadow-2xl"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${office.theme} opacity-95`}
                ></div>

                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:36px_36px] opacity-30"></div>

                <div className="relative z-10 p-7 md:p-8">
                  <div className="flex items-center justify-center w-16 h-16 text-2xl bg-white text-slate-950 rounded-2xl">
                    <i className={office.icon}></i>
                  </div>

                  <p className="mt-6 text-xs font-black tracking-widest uppercase text-white/80">
                    {office.label}
                  </p>

                  <h3 className="mt-2 text-3xl font-black text-white">
                    {office.title}
                  </h3>

                  <div className="mt-5 space-y-2">
                    {office.address.map((line) => (
                      <p
                        key={line}
                        className="text-base font-semibold leading-7 text-white/90"
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-6 mt-8 text-center bg-white border shadow-lg rounded-3xl border-slate-200">
            <p className="text-lg font-black text-slate-950">
              ✉️ Email:{" "}
              <a
                href="mailto:support@practicalinfosec.com"
                className="text-blue-700 underline"
              >
                support@practicalinfosec.com
              </a>{" "}
              | 📞 Phone:{" "}
              <a href="tel:+91-9080432374" className="text-blue-700 underline">
                +91-9080432374
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* SUPPORT TOPICS */}
      <section className="px-4 py-20 bg-slate-50 md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              How We Can Help
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              Contact Us for Course and Career Guidance
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {supportItems.map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 p-5 bg-white border shadow-sm rounded-2xl border-slate-200"
              >
                <span className="flex items-center justify-center w-10 h-10 text-sm text-white bg-blue-700 rounded-xl">
                  <i className="fa-solid fa-check"></i>
                </span>

                <p className="text-base font-black text-slate-800">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 py-20 bg-white md:px-20">
        <div className="relative max-w-6xl p-8 mx-auto overflow-hidden shadow-2xl rounded-[2rem] bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 md:p-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.25),transparent_35%)]"></div>

          <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                Ready to Start?
              </p>

              <h2 className="mt-3 text-3xl font-black text-white md:text-5xl">
                Choose the right cybersecurity training path.
              </h2>

              <p className="max-w-3xl mt-4 text-base font-semibold leading-7 text-slate-300">
                Explore our Junior and Senior Penetration Tester programs and
                get guided by our training team.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link
                to="/junior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105"
              >
                Apply for Junior
              </Link>

              <Link
                to="/senior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                Apply for Senior
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHATSAPP STICKY BUTTON */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setWhatsappOpen((prev) => !prev);
        }}
        className="fixed z-50 p-3 px-4 text-white transition -translate-y-1/2 rounded-full shadow-lg cursor-pointer top-1/2 right-6 animate-blink hover:opacity-90 bg-gradient-to-r from-green-400 via-green-500 to-green-600"
        aria-label="Join WhatsApp"
      >
        <i className="text-4xl fa-brands fa-whatsapp"></i>
      </button>

      {/* WHATSAPP MODAL */}
      {whatsappOpen && (
        <div
          className="fixed z-50 p-4 transition-opacity duration-300 -translate-y-1/2 bg-white shadow-xl top-1/2 right-20 rounded-xl w-72 max-sm:right-4 max-sm:left-4 max-sm:w-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsappTitle"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setWhatsappOpen(false);
            }}
            className="absolute text-3xl text-gray-600 cursor-pointer top-2 left-4 hover:text-red-500"
            aria-label="Close"
          >
            &times;
          </button>

          <h2
            id="whatsappTitle"
            className="mt-6 mb-2 text-lg font-bold text-center text-gray-800"
          >
            Unsure about which course to choose?
          </h2>

          <p className="mb-3 text-sm text-center text-gray-700">
            Tap below to start a WhatsApp chat — we&apos;re here to help!
          </p>

          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={openWhatsAppChat}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-white transition bg-green-600 rounded-full hover:bg-green-700"
              aria-label="Chat Now on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp"></i>
              Chat Now
            </button>

            <button
              type="button"
              onClick={openWhatsAppAppFallback}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-gray-800 transition border border-gray-200 rounded-full hover:bg-gray-50"
              aria-label="Open in WhatsApp App"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.52 3.48A11.93 11.93 0 0012 .5C5.73.5.99 5.24.99 11.5c0 2.01.53 3.95 1.53 5.68L0 24l6.97-1.79c1.61.45 3.28.68 4.9.68 6.27 0 11.01-4.74 11.01-11 0-3.02-1.18-5.85-3.36-7.41zM12 21.5c-1.44 0-2.86-.34-4.14-.99l-.3-.15-4.14 1.06 1.15-3.98-.18-.31A8.12 8.12 0 013 11.5c0-4.97 4.06-9 9-9 2.41 0 4.66.94 6.36 2.64a8.95 8.95 0 012.64 6.36c0 4.97-4.06 9-9 9z" />
              </svg>
              Open in App
            </button>
          </div>
        </div>
      )}

      {/* BACK TO TOP BUTTON */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed z-50 p-4 text-white transition bg-blue-700 rounded-lg shadow-lg cursor-pointer bottom-12 right-7 hover:bg-blue-800"
          aria-label="Back to top"
        >
          <i className="text-xl fa-solid fa-arrow-up"></i>
        </button>
      )}

      {/* FLOATING CALL BUTTON */}
      <a
        href="tel:+91-9080432374"
        className="fixed z-50 p-3 text-white transition rounded-md shadow-lg cursor-pointer bg-violet-600 bottom-60 right-7 hover:bg-green-700"
        aria-label="Call Us"
      >
        <i className="text-2xl fa-solid fa-phone"></i>
      </a>
    </main>
  );
}

export default ContactUs;